/* global React */
import { Grid } from '@mui/material'
import { preload, useMemoRef, useStyles } from '@platform/react/hook'
import Text from 'ui/Element/Text'
import { Suspense, useMemo } from 'react'
import ErrorBoundary from 'ui/Error'
import SimpleButton from 'ui/Element/Button'
import { PlatformEvent } from 'lib/util'

interface Conversation {
  /** the person's first name */
  firstName?: string
  /** the person's last name */
  lastName?: string
}

interface ConversationsProps {
  /** value to be set through an action or an event */
  value?: any
  /** whether to return null instead of the element */
  hidden?: boolean
  /** a label to display for the conversations */
  label?: string
  /** the template used to render each conversation */
  template?: any
  /** the component's event handlers */
  events: {
    /** obtains the conversations to display */
    onOpen?: (e: PlatformEvent | null) => { hidden?: boolean, data?: Conversation[] } | undefined
  }
}

/**
 * Returns the Conversations element's current theme styles.
 *
 * @param theme  the application's current theme
 * @returns      the Conversations element's styles
 */
const styles = (theme: any) => ({
  root: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing(1),
    borderBottom: `0.0625rem solid ${theme.palette.divider}`,
    '& *': {
      marginRight: theme.spacing(1),
    },
  },
  message: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    textAlign: 'center',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  ...theme.custom.systemMessage,
})

/**
 * Conversation helper function
 *
 * Returns a list of conversations for the current resource (e.g. ticket).
 *
 * @param p
 * @param p.classes        styles
 * @param p.conversations  list of recipients
 * @param p.Template       component for rendering each recipient
 * @returns                the rendered conversations
 * @private
 */
const _conversations = ({
  conversations,
  Template,
}: {
  classes?: Record<string, string>
  conversations: Conversation[]
  Template: any
}) => conversations.map((conversation, index) => {
  const recipient = `${conversation.firstName} ${conversation.lastName}`

  return (
    <Template
      key={index}
      fullWidth={false}
      value={recipient}
      variant={'contained'}
      color={'primary'}
      startIcon={{
        name: 'user',
      }}
      sx={{
        '& > .MuiButton-startIcon > div': {
          margin: 0,
        },
      }}
    />
  )
})

/**
 * Conversations
 *
 * Show a bar of currently ongoing conversations for the current ressource (e.g. ticket)
 *
 * TODO: Currently, only one recipient/conversation per ticket is allowed. Later on, we need this
 * component to function as a tab bar to switch between the different conversations.
 *
 * @param props          the component's properties
 * @param props.hidden   whether to show the component
 * @param props.label    the label
 * @param props.template the component to use for each conversation
 * @param props.events   incoming events
 * @returns              the new Conversations element, or null
 */
const Conversations = ({
  hidden = false,
  label,
  template = SimpleButton,
  events,
}: ConversationsProps) => {
  const { onOpen } = events
  const classes = useStyles(styles)() as Record<string, string>
  const Button = useMemo(() => preload(template), [])

  const { hidden: isEmpty, data: conversationList } = onOpen?.(null) || {}

  return hidden || isEmpty ? null : (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <Grid
          item
          className={classes.root}
        >
          <Text
            key={'label'}
            className={classes.message}
            variant={'body1'}
          >
            {label}
          </Text>
          {_conversations({ classes, conversations: conversationList || [], Template: Button })}
        </Grid>
      </Suspense>
    </ErrorBoundary>
  )
}

export default useMemoRef(Conversations, (props: ConversationsProps) => [props.value])
